/**
 * outbox — a durable queue (IndexedDB) of create operations performed while
 * offline. Each item is replayed to the server by the sync engine once the
 * connection returns.
 *
 * Item shape:
 *   { id, type, payload, createdAt, status: 'pending'|'error', error, tries }
 *
 * Best-effort storage: failures resolve to safe defaults so the app never
 * breaks because of IndexedDB issues.
 */
const DB_NAME = 'sellgo-outbox';
const STORE = 'ops';
const DB_VERSION = 1;

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB unavailable')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function run(mode, makeRequest) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const request = makeRequest(store);
        let result;
        if (request) request.onsuccess = () => { result = request.result; };
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }),
  );
}

function makeId() {
  return `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Add an operation to the queue. Returns the stored item. */
export async function enqueue(type, payload) {
  const item = {
    id: makeId(),
    type,
    payload,
    createdAt: Date.now(),
    status: 'pending',
    error: '',
    tries: 0,
  };
  try { await run('readwrite', (s) => s.add(item)); } catch { /* ignore */ }
  return item;
}

/** All items, oldest first. */
export async function listOps() {
  try {
    const all = (await run('readonly', (s) => s.getAll())) || [];
    return all.sort((a, b) => a.createdAt - b.createdAt);
  } catch { return []; }
}

/** Count of items, optionally filtered by status. */
export async function countOps(status) {
  const all = await listOps();
  return status ? all.filter((o) => o.status === status).length : all.length;
}

export async function removeOp(id) {
  try { await run('readwrite', (s) => s.delete(id)); } catch { /* ignore */ }
}

export async function updateOp(id, patch) {
  try {
    const current = await run('readonly', (s) => s.get(id));
    if (current) await run('readwrite', (s) => s.put({ ...current, ...patch }));
  } catch { /* ignore */ }
}

export async function clearOps() {
  try { await run('readwrite', (s) => s.clear()); } catch { /* ignore */ }
}
